import type { QueryClient } from '@tanstack/react-query'
import { boardQuery, projectsQuery, taskByKeyQuery } from './api/queries'

/**
 * Starts the page's own request before the router asks for it.
 *
 * Without this the app is a waterfall: load the bundle, check the session,
 * and only then fetch what the page is actually for. The session check is a
 * guard on rendering, not a prerequisite for the data - the cookie goes with
 * every request either way - so there is no reason for the second request to
 * wait for the first. Measured on a board of twelve tasks, that wait was
 * about half of the time to content.
 *
 * Read straight off `window.location` because it runs before the router
 * exists. It is a prefetch and nothing depends on it: a wrong guess costs one
 * request that nothing reads, and a signed-out visitor gets a 401 that is
 * never retried.
 */
export function warmStart(queryClient: QueryClient): void {
  const path = window.location.pathname

  // Nothing to warm for somebody who is not in yet.
  if (path === '/login') return

  // A 401 here is the ordinary answer for an expired session; the route
  // guard is what turns that into a redirect, so it is swallowed.
  const swallow = () => undefined

  const task = /^\/(?:project\/)?task\/([A-Za-z][A-Za-z0-9]*-\d+)$/.exec(path)
  if (task?.[1]) {
    void queryClient.prefetchQuery(taskByKeyQuery(task[1])).catch(swallow)

    return
  }

  const board = /^\/project\/([0-9a-f-]{36})(?:\/|$)/.exec(path)
  if (board?.[1]) {
    void queryClient.prefetchQuery(boardQuery(board[1])).catch(swallow)

    return
  }

  // Not for Settings, which shows no project list: warming one there is the
  // whole company's projects fetched for a screen that never draws them.
  const settings = new URLSearchParams(window.location.search).get('view') === 'settings'

  if ((path === '/project' && !settings) || path === '/') {
    void queryClient.prefetchQuery(projectsQuery(false)).catch(swallow)
  }
}
